<template>
  <SettingsLayout>
    <!-- Page header -->
    <div
      class="pb-4 pt-6 px-4 sm:p-6 bg-white flex-col space-y-2 items-center justify-between border-b border-b-gray-200"
    >
      <Icon
        name="arrow-left"
        size="custom"
        class="w-6 sm:w-0 sm:hidden -ml-0.5 text-gray-900"
        @click="handleBack"
      />
      <div class="flex items-center gap-2 justify-between">
        <h1 class="text-gray-900 text-xl font-medium">
          {{ t("admin.settings.messenger.title") }}
        </h1>
        <button
          class="flex text-base font-semibold text-white border border-button-primary-border bg-brand-solid rounded-md items-center gap-2 p-3.5 md:px-4 md:py-2.5"
          @click="openMessenger"
        >
          <Icon name="link-default" />
          <span v-if="!maxSmall">
            {{ t("admin.settings.messenger.open") }}
          </span>
        </button>
      </div>
    </div>

    <!-- Page content -->
    <div class="h-full relative inset-0">
      <div
        class="overflow-hidden sm:border sm:rounded-xl bg-white sm:m-6 absolute inset-0"
      >
        <div class="py-2 px-4">
          <Tabs :tabs="tabs" @select="handleSelect" />
        </div>
        <div class="border-t overflow-y-scroll h-full">
          <Component
            class="mb-12"
            :is="activeTab.component"
            v-bind="activeTab.props"
          />
        </div>
      </div>
    </div>
  </SettingsLayout>
</template>

<script setup>
import { Tabs } from "@/Components/Navigation/Tabs";
import { Messages, Settings } from "@/Pages/Settings/MessengerFlow/Tabs";
import PersistentLayout from "@/Layouts/PersistentLayout.vue";
import SettingsLayout from "@/Layouts/SettingsLayout.vue";
import { usePage } from "@inertiajs/vue3";
import { computed, ref } from "vue";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import { useI18n } from "vue-i18n";
import { Icon } from "@/SharedComponents/Icon";
import { useWebView } from "@/Composables/useWebView";
import { useBreakpoints } from "@/Composables/useBreakpoints";
import { mobileSatisfiesVersion } from "@/Utils/version";

const { t } = useI18n();
const { maxSmall } = useBreakpoints();

const tenant = usePage().props.tenant;

defineOptions({ layout: PersistentLayout });

const props = defineProps({
  screens: { type: Array, required: true },
  notification_options: { type: Array, required: true },
});

const tabs = ref([
  {
    title: t("components.organisation.messenger_flow.settings.title"),
    key: "settings",
    component: Settings,
    active: true,
    props: {
      tenant: tenant,
      notificationOptions: props.notification_options,
    },
  },
  {
    title: t("components.organisation.messenger_flow.messages.title"),
    key: "messages",
    component: Messages,
    active: false,
    props: {
      tenant: tenant,
      screens: props.screens,
      allowedLocales: Object.keys(tenant.settings.allowed_messenger_locales),
    },
  },
]);

const activeTab = computed(() => {
  return tabs.value.find((tab) => tab.active);
});

const handleSelect = (value) => {
  tabs.value.map((tab) => (tab.active = tab.key === value.key));
};

const { runningInWebView, openURL } = useWebView();

const openMessenger = () => {
  const url = route("space.select", [tenant]);

  if (!runningInWebView()) {
    window.open(url, "_blank");
    return;
  }

  if (mobileSatisfiesVersion("2.2.0")) {
    openURL(url);
    return;
  }

  window.open(url, "_blank");
};

function handleBack() {
  router.visit(route("settings.tenant", [tenant]));
}
</script>

<style lang="scss" scoped></style>
